import { Breadcrumbs, Card, Container, Link, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { NavigateNext } from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import { sgrColors } from '../../../ui/sgr/SgrColors';
import { useContext } from 'react';
import { BreadcrumbsTitleContext } from '../../../wrappers/AppWrapper';

/**
 * Path segments whose translation key differs from the segment itself
 */
const breadcrumbKeyOverrides: Record<string, string> = {
  stats: 'statistics_selection.title',
  watchbase: 'statistics_selection.watchbase_title',
  businessbase: 'statistics_selection.businessbase_title',
  names: 'entity_statistics.names_tab',
  monitored_names: 'entity_statistics.monitored_names_tab',
  tags: 'entity_statistics.tags_tab',
  entities: 'entity_statistics.entities_tab',
  evidences: 'entity_statistics.evidences_tab',
  most_searched: 'business_base_statistics.most_searched_tab',
  most_requested: 'business_base_statistics.most_requested_tab',
};

const DynamicBreadcrumbs = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { customerName, applicationName, customDopFileName, walletName } = useContext(
    BreadcrumbsTitleContext
  );
  const isNumber = (str: string) => /^[+-]?\d+(\.\d+)?$/.test(str);

  const allPathnames = location.pathname.split('/').filter((x) => x);

  const pathnames = allPathnames.filter((x) => x !== 'applications' && x !== 'wallets');

  const getBreadcrumbTitle = (value: string, index: number) => {
    /**
     * If the value is a number, it means that it is an id and
     * we should return the name of the customer/application/dop
     * that corresponds to that id instead
     */
    if (isNumber(value)) {
      if (pathnames[index - 1] === 'api_customers') {
        return customerName !== ''
          ? customerName
          : localStorage.getItem('customerName');
      } else if (allPathnames[index] === 'applications') {
        return applicationName !== ''
          ? applicationName
          : localStorage.getItem('applicationName');
      } else if (allPathnames.includes('wallets') && location.pathname.includes('wallets')) {
        return walletName !== ''
          ? walletName
          : localStorage.getItem('walletName');
      } else if (pathnames[index - 1] === 'custom_dops') {
        return customDopFileName !== ''
          ? `File ${customDopFileName}`
          : `File ${localStorage.getItem('customDopFileName')}`;
      }
    }
    
    const translatedValue = t(breadcrumbKeyOverrides[value] ?? value);
    return typeof translatedValue === 'string' ? translatedValue : value;
  };

  return (
    <Container>
      <Card
        sx={{
          backgroundColor: 'white',
          padding: { xs: '0.5rem', sm: '1rem' },
          marginBottom: '1.5rem',
        }}
      >
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          aria-label="breadcrumb"
          sx={{
            fontSize: { xs: '0.8rem', sm: 'inherit' },
            '& .MuiBreadcrumbs-ol': { flexWrap: 'wrap' },
            '& .MuiBreadcrumbs-li': { minWidth: 0, overflowWrap: 'anywhere' },
          }}
        >
          <Link
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: 'none',
              fontWeight: pathnames.length === 0 ? 600 : 400,
              color:
                pathnames.length === 0 ? sgrColors.breadcrumb_bold : 'inherit',
              '&:hover': {
                color: pathnames.length !== 0 ? sgrColors.azure : 'none',
              },
            }}
          >
            <Stack
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                gap: '5px',
              }}
            >
              <HomeIcon
                sx={{
                  position: 'relative',
                  top: '-2px',
                }}
              />{' '}
              {t('home')}
            </Stack>
          </Link>
          {pathnames.map((value, index) => {
            const routeTo = `/${pathnames.slice(0, index + 1).join('/')}`;
            const isLast = index === pathnames.length - 1;

            return (
              <Link
                sx={{
                  textDecoration: 'none',
                  '&:hover': {
                    color: isLast ? 'none' : sgrColors.azure,
                  },
                  fontWeight: isLast ? 600 : 500,
                }}
                key={routeTo}
                component={RouterLink}
                to={routeTo}
                color={isLast ? sgrColors.breadcrumb_bold : 'inherit'}
              >
                {getBreadcrumbTitle(value, index)}
              </Link>
            );
          })}
        </Breadcrumbs>
      </Card>
    </Container>
  );
};

export default DynamicBreadcrumbs;
